
import { useState } from "react";
import PageTransition from "@/components/PageTransition";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { FaSearch, FaLink, FaImage, FaSpinner } from "react-icons/fa";
import AnalysisCard from "@/components/AnalysisCard";
import { analyzeContent, AnalysisResult } from "@/lib/api";

const Detect = () => {
  const [activeTab, setActiveTab] = useState("text");
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [image, setImage] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const { toast } = useToast();

  const handleTextAnalysis = async () => {
    if (!text.trim()) {
      toast({
        title: "Error",
        description: "Please enter some text to analyze",
        variant: "destructive",
      });
      return;
    }

    setIsAnalyzing(true);
    try {
      const result = await analyzeContent({ text });
      setAnalysisResult(result);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to analyze text. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleUrlAnalysis = async () => {
    if (!url.trim()) {
      toast({
        title: "Error",
        description: "Please enter a URL to analyze",
        variant: "destructive",
      });
      return;
    }

    // Simple URL validation
    if (!url.match(/^(https?:\/\/)?.+\..+/)) {
      toast({
        title: "Invalid URL",
        description: "Please enter a valid URL starting with http:// or https://",
        variant: "destructive",
      });
      return;
    }

    setIsAnalyzing(true);
    try {
      const result = await analyzeContent({ url });
      setAnalysisResult(result);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to analyze URL. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid file",
        description: "Please upload an image file",
        variant: "destructive",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target?.result as string;
      setImage(base64);
    };
    reader.readAsDataURL(file);
  };

  const handleImageAnalysis = async () => {
    if (!image) {
      toast({
        title: "Error",
        description: "Please upload an image to analyze",
        variant: "destructive",
      });
      return;
    }

    setIsAnalyzing(true);
    try {
      const result = await analyzeContent({ image });
      setAnalysisResult(result);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to analyze image. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <PageTransition>
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-900 to-black py-6">
        <div className="w-full max-w-4xl lg:max-w-5xl xl:max-w-6xl space-y-8 px-4 md:px-6">
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-truth to-blue-300 bg-clip-text text-transparent">Analyze Content</h1>
            <p className="mt-2 text-gray-400">Upload or paste content to detect misinformation</p>
          </div>
          <Card className="bg-black/30 backdrop-blur-lg border-gray-800 shadow-[0_4px_15px_rgba(0,0,0,0.3)]">
            <CardHeader>
              <CardTitle className="text-lg sm:text-xl text-white">Content Analysis</CardTitle>
              <CardDescription className="text-xs sm:text-sm">AI-powered analysis of text, images, and videos</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid grid-cols-3 mb-4 bg-black/50">
                  <TabsTrigger value="text" className="flex items-center gap-2 data-[state=active]:bg-truth">
                    <FaSearch className="h-4 w-4" /> Text
                  </TabsTrigger>
                  <TabsTrigger value="url" className="flex items-center gap-2 data-[state=active]:bg-truth">
                    <FaLink className="h-4 w-4" /> URL
                  </TabsTrigger>
                  <TabsTrigger value="image" className="flex items-center gap-2 data-[state=active]:bg-truth">
                    <FaImage className="h-4 w-4" /> Image
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="text">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="text-input">Enter text to analyze</Label>
                      <Textarea
                        id="text-input"
                        placeholder="Paste article text, social media post, or any content you want to analyze..."
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        rows={10}
                        className="resize-none bg-black/20 border-gray-700"
                      />
                    </div>
                    <Button
                      onClick={handleTextAnalysis}
                      className="w-full bg-gradient-to-r from-truth to-blue-600 hover:opacity-90 transition-all"
                      disabled={isAnalyzing || !text.trim()}
                    >
                      {isAnalyzing ? (
                        <>
                          <FaSpinner className="h-4 w-4 mr-2 animate-spin" /> Analyzing...
                        </>
                      ) : (
                        "Analyze Text"
                      )}
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="url">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="url-input">Enter URL to analyze</Label>
                      <Input
                        id="url-input"
                        type="url"
                        placeholder="https://example.com/article"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        className="bg-black/20 border-gray-700"
                      />
                      <p className="text-xs text-gray-400">
                        Enter the URL of an article, blog post, or news item you want to analyze.
                      </p>
                    </div>
                    <Button
                      onClick={handleUrlAnalysis}
                      className="w-full bg-gradient-to-r from-truth to-blue-600 hover:opacity-90 transition-all"
                      disabled={isAnalyzing || !url.trim()}
                    >
                      {isAnalyzing ? (
                        <>
                          <FaSpinner className="h-4 w-4 mr-2 animate-spin" /> Analyzing URL...
                        </>
                      ) : (
                        "Analyze URL"
                      )}
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="image">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="image-input">Upload image to analyze</Label>
                      <div className="flex items-center">
                        <Input
                          id="image-input"
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                        <Button
                          asChild
                          variant="outline"
                          className="w-full h-32 flex flex-col items-center justify-center border-dashed border-gray-700 bg-black/20 hover:bg-black/30"
                        >
                          <label htmlFor="image-input" className="cursor-pointer">
                            {image ? (
                              <div className="relative w-full h-full flex items-center justify-center">
                                <img
                                  src={image}
                                  alt="Preview"
                                  className="max-h-28 max-w-full object-contain"
                                />
                              </div>
                            ) : (
                              <>
                                <FaImage className="h-8 w-8 mb-2 text-gray-400" />
                                <span>Click to upload image</span>
                                <span className="text-xs text-gray-500 mt-1">
                                  JPG, PNG, GIF up to 5MB
                                </span>
                              </>
                            )}
                          </label>
                        </Button>
                      </div>
                    </div>
                    <Button
                      onClick={handleImageAnalysis}
                      className="w-full bg-gradient-to-r from-truth to-blue-600 hover:opacity-90 transition-all"
                      disabled={isAnalyzing || !image}
                    >
                      {isAnalyzing ? (
                        <>
                          <FaSpinner className="h-4 w-4 mr-2 animate-spin" /> Analyzing Image...
                        </>
                      ) : (
                        "Analyze Image"
                      )}
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <div className="space-y-4">
            {analysisResult ? (
              <div className="animate-fade-in">
                <AnalysisCard result={analysisResult} isLoading={isAnalyzing} />
              </div>
            ) : (
              <Card className="bg-black/30 backdrop-blur-lg border-gray-800 shadow-[0_4px_15px_rgba(0,0,0,0.3)] h-full flex flex-col justify-center items-center p-8 text-center">
                <CardContent>
                  <div className="flex flex-col items-center justify-center space-y-4">
                    <FaSearch className="h-12 w-12 text-gray-500 mb-4" />
                    <h3 className="text-xl font-medium">No Analysis Yet</h3>
                    <p className="text-gray-400">
                      Enter text, URL, or upload an image to start analyzing content for potential misinformation.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <Separator className="my-8" />

          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">How It Works</h2>
              <p className="text-gray-400 mt-1">
                Our AI system analyzes content using several factors to determine potential misinformation.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <InfoCard
                title="NLP Analysis"
                description="Natural Language Processing identifies linguistic patterns common in misinformation, such as sensationalism, emotional manipulation, and inconsistencies."
              />
              <InfoCard
                title="Source Credibility"
                description="We evaluate the trustworthiness of content sources based on historical accuracy, domain reputation, transparency, and more."
              />
              <InfoCard
                title="Factual Verification"
                description="Our system checks claims against a database of verified facts and known misinformation to provide accurate assessments."
              />
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.5s ease-out;
        }
        `}
      </style>
    </PageTransition>
  );
};

interface InfoCardProps {
  title: string;
  description: string;
}

function InfoCard({ title, description }: InfoCardProps) {
  return (
    <Card className="bg-black/30 backdrop-blur-lg border-gray-800 shadow-[0_4px_15px_rgba(0,0,0,0.3)] hover:border-truth/30 transition-colors">
      <CardHeader>
        <CardTitle className="text-lg text-white">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-gray-400">{description}</p>
      </CardContent>
    </Card>
  );
}

export default Detect;
